import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { MessageKey } from '../../../models/message/message-key';
import { FlightSearchFormsSelectors } from '../../../shared/flight-search-forms/state/flight-search-forms.selectors';
import { RootState } from '../../../state/state';
import { GlobalEvent, GlobalEventService } from '../../global-event-service/global-event.service';
import { addGlobalJetStreamMessage } from '../../message-service/state/message.actions';
import { FlightShoppingService } from '../flight-shopping.service';
import {
  loadFlightShoppingFrapiResultsComplete,
  loadFlightShoppingFrapiResultsInitialBooking,
  loadFlightShoppingResults,
  loadFlightShoppingResultsComplete,
} from './flight-shopping-service.actions';

@Injectable()
export class FlightShoppingServiceEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly flightShoppingService = inject(FlightShoppingService);
  private readonly router = inject(Router);
  private readonly globalEventService = inject(GlobalEventService);

  loadFlightShoppingResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFlightShoppingResults),
      mergeMap((action) =>
        this.flightShoppingService.getFlightShoppingResults(action.request).pipe(
          map((result) => {
            return result.response
              ? loadFlightShoppingResultsComplete(result.success, result.response)
              : loadFlightShoppingResultsComplete(result.success, null);
          })
        )
      ),
      catchError(() => of(loadFlightShoppingResultsComplete(false, null)))
    )
  );

  loadFlightShoppingFrapiResultsInitialBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFlightShoppingFrapiResultsInitialBooking),
      mergeMap((action) =>
        this.store.select(FlightSearchFormsSelectors.getFlightSearchForm(action.featureId)).pipe(
          take(1),
          mergeMap((flightSearchForm) => {
            this.globalEventService.broadcastAjax(GlobalEvent.AJAX_START);

            return this.flightShoppingService.getFlightShoppingFrapi(action.featureId, action.request).pipe(
              map((result) => {
                this.globalEventService.broadcastAjax(GlobalEvent.AJAX_END);

                if (!flightSearchForm) {
                  // State is empty: show error on current page, do NOT navigate
                  return loadFlightShoppingFrapiResultsComplete(result, action.request.sliceToSearchIndex);
                }

                if (!result?.success) {
                  // State is present: navigate back and show error
                  this.router.navigateByUrl(`/book-flights/flight-search/${action.tripType}`).then(() => {
                    this.store.dispatch(addGlobalJetStreamMessage(MessageKey.INITIAL_BOOKING_NO_FLIGHT_RESULTS));
                  });
                }

                return loadFlightShoppingFrapiResultsComplete(result, action.request.sliceToSearchIndex);
              }),
              catchError((err: HttpErrorResponse) => {
                this.globalEventService.broadcastAjax(GlobalEvent.AJAX_END);

                if (!flightSearchForm) {
                  // State is empty: show error on current page
                  this.store.dispatch(addGlobalJetStreamMessage(MessageKey.INITIAL_BOOKING_NO_FLIGHT_RESULTS));
                  return of(
                    loadFlightShoppingFrapiResultsComplete({ id: action.featureId, success: false }, action.request.sliceToSearchIndex)
                  );
                }

                // State is present: navigate back and show error
                this.router.navigateByUrl(`/book-flights/flight-search/${action.tripType}`).then(() => {
                  this.store.dispatch(addGlobalJetStreamMessage(MessageKey.INITIAL_BOOKING_NO_FLIGHT_RESULTS));
                });
                return of(
                  loadFlightShoppingFrapiResultsComplete({ id: action.featureId, success: false }, action.request.sliceToSearchIndex)
                );
              })
            );
          })
        )
      )
    )
  );
}
